/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Route Focus Management (WCAG 2.1 AA, SC 2.4.3 Focus Order)
 *
 * Single-page apps don't trigger the browser's default "focus the document
 * after navigation" behaviour, so screen reader users would otherwise be
 * stranded mid-page after a route change. This hook moves focus to the new
 * page's heading on every pathname change so SRs announce the new context.
 *
 * Strategy: prefer the first <h1> inside <main>; fall back to <main> itself
 * (which is tabIndex={-1}). The initial mount is skipped — the browser
 * handles initial page focus and we don't want to override the URL fragment
 * target the user may have arrived at.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MAIN_CONTENT_ID } from "../components/SkipToMainContent";

interface UseRouteFocusOptions {
  /**
   * Pause the hook (e.g. while the skip-to-main link is the active focus
   * target so we don't yank it away). Note: toggling false→true mid-session
   * still leaves `isFirstRender` false, so re-enabling immediately fires a
   * focus jump on the next pathname change. Acceptable for the current
   * RouteFocusManager (always enabled), but worth knowing if you toggle.
   */
  enabled?: boolean;
}

export const useRouteFocus = ({ enabled = true }: UseRouteFocusOptions = {}) => {
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    // The browser already handles focus on initial document load. Skip the
    // first effect so we don't override an in-page anchor target.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Defer to any open Radix-style focus trap (Dialog, Sheet, Alert
    // Dialog). Yanking focus to <main> while a modal is open would break
    // out of the trap and confuse SR users mid-flow. Once the modal closes
    // a subsequent navigation will fire useRouteFocus normally.
    const trappedOverlay = document.querySelector(
      '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]'
    );
    if (trappedOverlay) return;

    const main = document.getElementById(MAIN_CONTENT_ID);
    if (!main) return;

    // Prefer the page's <h1>; fall back to the <main> landmark.
    const heading = main.querySelector<HTMLElement>("h1");
    const target = heading ?? main;

    // <h1> isn't focusable by default, so add a tabindex if we're focusing
    // a heading. Cleaned up after focus moves to keep DOM tidy.
    const wasFocusable = target.hasAttribute("tabindex");
    if (!wasFocusable) {
      target.setAttribute("tabindex", "-1");
    }
    target.focus({ preventScroll: false });
    if (!wasFocusable && target !== main) {
      // Remove tabindex so subsequent Tab navigation skips the heading.
      // <main> keeps its tabindex={-1} (set in SidebarLayout) for the skip
      // link to keep working.
      const removeTabindex = () => {
        target.removeAttribute("tabindex");
        target.removeEventListener("blur", removeTabindex);
      };
      target.addEventListener("blur", removeTabindex);
    }
  }, [pathname, enabled]);
};
